// Smart Assist must only see the smart-chart fields the clinician has
// actually selected in this modal, not the full raw chart history payload.
// This mirrors the field-selection logic already used to build the note
// preview (see PrechartingModal's `selectedFieldValues`), but keeps values
// as structured data instead of formatted preview strings.

export interface SmartAssistChapiField {
  key: string;
  aliases?: string[];
}

export type SmartAssistChartHistorySource = Record<string, unknown> | null | undefined;
export type SmartAssistFieldSelections = Record<string, boolean> | null | undefined;

export const hasMeaningfulChartHistoryValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "number" || typeof value === "boolean") return true;
  return String(value).trim().length > 0;
};

/**
 * Builds the chart-history object to send to Smart Assist, limited to the
 * fields the clinician has selected. Any field the clinician has not
 * checked (or that has no meaningful value) is omitted entirely.
 */
export const buildSelectedSmartAssistChartHistory = (
  chartHistorySource: SmartAssistChartHistorySource,
  selections: SmartAssistFieldSelections,
  fields: SmartAssistChapiField[]
): Record<string, unknown> => {
  const source =
    chartHistorySource && typeof chartHistorySource === "object" ? chartHistorySource : {};
  const selectionMap = selections && typeof selections === "object" ? selections : {};

  return fields.reduce<Record<string, unknown>>((selected, field) => {
    if (!field?.key || !selectionMap[field.key]) {
      return selected;
    }

    const aliases =
      Array.isArray(field.aliases) && field.aliases.length > 0 ? field.aliases : [field.key];

    aliases.forEach((alias) => {
      const value = (source as Record<string, unknown>)[alias];
      if (hasMeaningfulChartHistoryValue(value)) {
        selected[alias] = value;
      }
    });

    return selected;
  }, {});
};
